/**
 * Aviso de conexão perdida, com botão de reconectar (PRD §11.2, item 4).
 *
 * Antes disto a queda era anunciada em duas linhas discretas — "desconectado" no canto do
 * HUD e um pedido de F5 no chat — enquanto o personagem continuava andando por predição
 * num mundo morto, câmera junto. Quem estava olhando para o combate não via nenhuma das
 * duas e só descobria que caiu quando percebia que nada mais reagia.
 *
 * O painel cobre o mundo de propósito: a queda é o único evento em que **não** há mais
 * nada de útil para ver ali, e esconder o cenário é o que impede de continuar jogando um
 * jogo que já parou.
 *
 * O botão pede uma sessão nova ao Login Server em vez de reabrir o socket com o token
 * antigo — o token de jogo é de uso único (PRD §6), então reusá-lo seria recusado no
 * primeiro frame e o jogador veria a mesma tela de novo sem entender por quê.
 */
export interface ConnectionBannerHandlers {
  onReconnect(): void;
}

export class ConnectionBanner {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly status: HTMLElement;
  private readonly retry: HTMLButtonElement;
  private readonly buttons: HTMLElement;

  constructor(root: HTMLElement, handlers: ConnectionBannerHandlers) {
    this.root = root;
    this.root.className = 'connection-banner';
    this.root.innerHTML = '';

    const panel = document.createElement('div');
    panel.className = 'connection-panel';

    const title = document.createElement('h2');
    title.className = 'connection-title';
    title.textContent = 'Conexão perdida';
    this.title = title;

    this.status = document.createElement('p');
    this.status.className = 'connection-status';

    this.retry = document.createElement('button');
    this.retry.type = 'button';
    this.retry.className = 'connection-button';
    this.retry.textContent = 'Reconectar';
    this.retry.addEventListener('click', () => handlers.onReconnect());

    const reload = document.createElement('button');
    reload.type = 'button';
    reload.className = 'connection-button ghost';
    reload.textContent = 'Voltar ao login';
    reload.addEventListener('click', () => location.reload());

    this.buttons = document.createElement('div');
    this.buttons.className = 'connection-buttons';
    this.buttons.append(this.retry, reload);

    panel.append(title, this.status, this.buttons);
    this.root.append(panel);

    this.hide();
  }

  show(reason = 'O servidor encerrou a conexão.'): void {
    this.title.textContent = 'Conexão perdida';
    this.buttons.hidden = false;
    this.status.textContent = reason;
    this.retry.disabled = false;
    this.root.classList.add('visible');
  }

  /**
   * Fila de entrada (PRD §6): a região está cheia e a conexão está viva, esperando vaga.
   *
   * Reaproveita o painel da queda em vez de ganhar um próprio porque a situação do jogador
   * é a mesma nas duas — o mundo não está lá para ser visto, e continuar olhando para ele
   * só faria pensar que o jogo travou. O que muda são os botões: aqui não há o que tentar
   * de novo, e um "Reconectar" à vista convidaria a fechar e reabrir, que é exatamente o
   * gesto que faria perder o lugar na fila.
   */
  showQueue(position: number, total: number): void {
    this.title.textContent = 'Fila de entrada';
    this.buttons.hidden = true;
    this.status.textContent =
      position === 1
        ? `Você é o próximo a entrar (${total} na fila). Não feche esta aba.`
        : `Posição ${position} de ${total}. Não feche esta aba — sair perde o lugar.`;
    this.root.classList.add('visible');
  }

  /** Tentativa em curso: o botão trava para não pedir duas sessões de uma vez. */
  setBusy(text: string): void {
    this.status.textContent = text;
    this.retry.disabled = true;
  }

  /** Falhou: o motivo fica na tela e o botão volta, porque tentar de novo costuma valer. */
  setError(text: string): void {
    this.status.textContent = text;
    this.retry.disabled = false;
  }

  hide(): void {
    this.root.classList.remove('visible');
  }
}
